import React from 'react';
import { Badge } from '@/Components/ui/badge';
import { Separator } from '@/Components/ui/separator';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';

export const StepIndicator = ({ 
  currentStep, 
  totalSteps, 
  steps, 
  onStepClick 
}) => {
  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'pending';
  };

  return (
    <div className="w-full mb-8">
      {/* Scrollable horizontal layout */}
      <div className="overflow-x-auto pb-3 pt-3">
        <div className="flex items-center justify-center gap-3 min-w-0 max-w-full">
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            const isClickable = onStepClick && (status === 'completed' || status === 'current');
            
            return (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center flex-shrink-0" style={{ minWidth: '80px', maxWidth: '100px' }}>
                  <button
                    type="button"
                    onClick={() => isClickable && onStepClick(index)}
                    disabled={!isClickable}
                    className={cn(
                      "flex flex-col items-center gap-2 transition-all duration-200 w-full",
                      isClickable && "cursor-pointer hover:opacity-80",
                      !isClickable && "cursor-not-allowed opacity-60"
                    )}
                  >
                    <div
                      className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-200 flex-shrink-0",
                        status === 'completed' && "bg-primary text-primary-foreground",
                        status === 'current' && "bg-primary text-primary-foreground ring-4 ring-primary/20",
                        status === 'pending' && "bg-muted text-muted-foreground border-2 border-muted-foreground/20"
                      )}
                      style={{
                        backgroundColor: status === 'completed' || status === 'current' 
                          ? getColor('primary') 
                          : undefined,
                        color: status === 'completed' || status === 'current'
                          ? getColor('primaryForeground')
                          : undefined,
                      }}
                    >
                      {status === 'completed' ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="text-center w-full px-1">
                      <div
                        className={cn(
                          "text-xs font-medium transition-colors truncate w-full leading-tight",
                          status === 'current' && "text-primary font-semibold",
                          status === 'completed' && "text-muted-foreground",
                          status === 'pending' && "text-muted-foreground"
                        )}
                        style={{
                          color: status === 'current' ? getColor('primary') : undefined,
                        }}
                        title={`${step.title}${step.description ? ` - ${step.description}` : ''}`}
                      >
                        {step.title}
                      </div>
                    </div>
                  </button>
                </div>
                {index < totalSteps - 1 && (
                  <div
                    className={cn(
                      "w-8 h-0.5 transition-colors flex-shrink-0",
                      index < currentStep ? "bg-primary" : "bg-muted"
                    )}
                    style={{
                      backgroundColor: index < currentStep ? getColor('primary') : undefined,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};
